import React, { useContext, useEffect, useRef, useState } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import TabNavigation from "../ui/TabNavigation";
import TradesTable from "../table/TradesTable";
import PositionsTable from "../table/PositionsTable";
import { PositionsDataContext } from "../../context/PositionsDataContext";
import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";
import { getEventDrivenPnlTableColumns } from "../table/columnsConfig/pnlTable";
import { CardWrapper } from "../ui";

const VolArb_PNL = () => {
  const checkAndClearStorageIfNewDay = () => {
    const lastVisitDate = localStorage.getItem("volArbLastVisitDate");
    const currentDate = new Date().toDateString();

    if (lastVisitDate !== currentDate) {
      localStorage.clear();
      localStorage.setItem("volArbLastVisitDate", currentDate);
    }
  };

  const theme = useTheme();
  const { datasets } = useContext(PositionsDataContext);
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const [positionsFilter, setPositionsFilter] = useState(() => {
    const saved = localStorage.getItem("volArbPnlFilter");
    return !saved || saved === "undefined" ? "" : saved;
  });
  const [positionsSorting, setPositionsSorting] = useState(() => {
    const savedSorting = localStorage.getItem("volArbPositionsTableSorting");
    return savedSorting
      ? JSON.parse(savedSorting)
      : [{ id: "StrategyName", desc: false }];
  });
  const [positionsScrollPosition, setPositionsScrollPosition] = useState(() => {
    return (
      parseInt(
        localStorage.getItem("volArbPositionsTableScrollPosition"),
        10,
      ) || 0
    );
  });

  const [allData, setAllData] = useState([]);
  const [maPositions, setMaPositions] = useState([]);
  const [tradedData, setTradedData] = useState([]);
  const [activeTab, setActiveTab] = useState("tab1");

  const [expandedPnLRows, setExpandedPnLRows] = useState(() => {
    checkAndClearStorageIfNewDay();
    const saved = localStorage.getItem("volArbPnLTableExpandedRows");
    return saved ? JSON.parse(saved) : {};
  });

  const positionsTableContainerRef = useRef(null);

  useEffect(() => {
    if (datasets?.positions) setAllData(datasets.positions);
  }, [datasets]);

  useEffect(() => {
    if (allData?.length > 0) {
      const volArbPositions = allData.filter((position) =>
        ["Vol Arbitrage"].includes(position.StrategyType),
      );
      setMaPositions(volArbPositions);

      const positionsWithTrades = allData.filter(
        (position) =>
          (position.buy_trade_quantity ||
            position.sell_trade_quantity ||
            position.short_trade_quantity ||
            position.cover_trade_quantity) &&
          ["Vol Arbitrage"].includes(position.StrategyType),
      );
      setTradedData(positionsWithTrades);
    }
  }, [allData]);

  useEffect(() => {
    if (activeTab === "tab3") {
      setTimeout(() => {
        const container = document.querySelector(
          '[class*="MuiTableContainer-root"]',
        );
        if (container) {
          const savedScroll =
            parseInt(
              localStorage.getItem("volArbPositionsTableScrollPosition"),
              10,
            ) || 0;
          container.scrollTop = savedScroll;

          const handleScroll = () => {
            setPositionsScrollPosition(container.scrollTop);
            localStorage.setItem(
              "volArbPositionsTableScrollPosition",
              container.scrollTop,
            );
          };
          container.addEventListener("scroll", handleScroll);
          return () => container.removeEventListener("scroll", handleScroll);
        }
      }, 200);
    }
  }, [activeTab, setPositionsScrollPosition]);

  const pnlTableConfig = {
    columns: getEventDrivenPnlTableColumns(theme),
    data: maPositions,
    enableColumnDragging: false,
    enableGrouping: true,
    enableSorting: true,
    enablePagination: false,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnResizing: true,
    enableBottomToolbar: false,
    enablePinning: true,
    columnResizeMode: "onChange",
    groupedColumnMode: "remove",
    enableTopToolbar: false,
    enableColumnActions: false,
    initialState: {
      grouping: ["StrategyName"],
      sorting: [{ id: "total_pnl", desc: true }],
      density: "compact",
      expanded: expandedPnLRows,
      columnPinning: isDesktop ? { left: ["StrategyName"] } : {},
    },
    state: {
      expanded: expandedPnLRows,
    },
    onExpandedChange: (updater) => {
      const newExpanded =
        typeof updater === "function" ? updater(expandedPnLRows) : updater;
      setExpandedPnLRows(newExpanded);
      localStorage.setItem(
        "volArbPnLTableExpandedRows",
        JSON.stringify(newExpanded),
      );
    },
    displayColumnDefOptions: {
      "mrt-row-expand": {
        Cell: ({ row }) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              paddingLeft: `${row.depth * 2}rem`,
              cursor: "pointer",
            }}
            onClick={row.getToggleExpandedHandler()}
          >
            {row.getIsExpanded() ? (
              <RemoveIcon sx={{ fontSize: { xs: "14px", sm: "16px" } }} />
            ) : (
              <AddIcon sx={{ fontSize: { xs: "14px", sm: "16px" } }} />
            )}
            {row.getIsGrouped() && (
              <span
                style={{
                  marginLeft: "8px",
                  fontWeight: "bold",
                  textAlign: "left",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                }}
              >
                {row.groupingValue}
              </span>
            )}
          </div>
        ),
      },
    },
    muiTableHeadCellProps: {
      sx: {
        fontWeight: 600,
        whiteSpace: "normal",
        backgroundColor: theme.palette.primary.dark,
        color: theme.palette.primary.contrastText,
        fontSize: {
          xs: "0.7rem",
          sm: "0.8rem",
        },
        letterSpacing: "0.3px",
        borderBottom: `2px solid ${theme.palette.divider}`,
        padding: {
          xs: theme.spacing(0.5, 0.25, 0.25, 0.25),
          sm: theme.spacing(0.75, 0.5, 0.25, 0.5),
        },
        verticalAlign: "bottom",
        lineHeight: "1.1",
        minHeight: {
          xs: "40px",
          sm: "35px",
        },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        padding: {
          xs: theme.spacing(0.5),
          sm: theme.spacing(1),
        },
        fontSize: {
          xs: "0.75rem",
          sm: "0.875rem",
        },
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
    },
    muiTableBodyRowProps: {
      sx: {
        backgroundColor: theme.palette.background.paper,
        padding: "0px",
        height: {
          xs: "30px",
          sm: "25px",
        },
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.action.hover,
        },
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        height: {
          xs: "calc(100vh - 200px)",
          sm: "calc(100vh - 130px)",
        },
        marginBottom: "0px",
        width: "100%",
        maxWidth: "100%",
        overflowX: "auto",
        overflowY: "auto",
        position: "relative",
        borderRadius: 0,
        boxShadow: theme.shadows[1],
        backgroundColor: theme.palette.background.default,
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "8px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.grey[100],
          borderRadius: 0,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.grey[300],
          borderRadius: 0,
          "&:hover": {
            backgroundColor: theme.palette.grey[400],
          },
        },
      },
    },
    muiTableProps: {
      sx: {
        width: "100%",
        maxWidth: "100%",
        tableLayout: "auto",
        "& .MuiTableHead-root": {
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
        "& .MuiTableHead-root .MuiTableRow-root": {
          height: "auto",
          minHeight: {
            xs: "40px",
            sm: "35px",
          },
        },
        "& .MuiTableHead-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(0.5, 0.25, 0.25, 0.25),
            sm: theme.spacing(0.75, 0.5, 0.25, 0.5),
          },
          "& .Mui-TableHeadCell-Content": {
            width: "100%",
            maxWidth: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
            display: "flex",
            alignItems: "center",
          },
          "& .Mui-TableHeadCell-Content-Labels": {
            width: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
          },
          "& .Mui-TableHeadCell-Content-Wrapper": {
            whiteSpace: "normal !important",
            textOverflow: "unset",
            overflow: "visible",
          },
        },
        "& .MuiTableFooter-root": {
          borderTop: `2px solid ${theme.palette.divider}`,
          position: "sticky",
          bottom: 0,
          zIndex: 100,
          backgroundColor: theme.palette.primary.dark,
        },
        "& .MuiTableFooter-root .MuiTableRow-root": {
          height: "40px",
        },
        "& .MuiTableFooter-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(1, 0.5),
            sm: theme.spacing(1.5, 1),
          },
          lineHeight: "1.2",
          backgroundColor: theme.palette.primary.dark,
          fontSize: {
            xs: "0.75rem",
            sm: "0.875rem",
          },
          color: theme.palette.primary.contrastText,
        },
      },
    },
    muiTableFooterRowProps: {
      sx: {
        backgroundColor: theme.palette.primary.dark,
        color: theme.palette.primary.light,
        fontWeight: 600,
        position: "sticky",
        bottom: 0,
        zIndex: 100,
        borderTop: `2px solid ${theme.palette.divider}`,
        height: "40px",
      },
    },
  };

  const tabs = [
    { value: "tab1", label: "Vol Arb P&L" },
    { value: "tab2", label: "Vol Arb Trades Today" },
    { value: "tab3", label: "Vol Arb Positions" },
  ];

  const pnl_table = useMaterialReactTable(pnlTableConfig);

  return (
    <Box sx={{ width: "100%" }}>
      <CardWrapper sx={{ mb: 2, mt: 2 }}>
        <TabNavigation
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </CardWrapper>

      <Box sx={{ backgroundColor: theme.palette.background.default }}>
        {activeTab === "tab1" && (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              backgroundColor: theme.palette.background.paper,
              borderRadius: 0,
              boxShadow: theme.shadows[1],
            }}
          >
            <MaterialReactTable table={pnl_table} />
          </Box>
        )}
        {activeTab === "tab2" && (
          <TradesTable
            data={tradedData}
            title="Vol Arb Trades"
            globalFilter={positionsFilter}
            setGlobalFilter={setPositionsFilter}
            sorting={positionsSorting}
            setSorting={setPositionsSorting}
            scrollPosition={positionsScrollPosition}
          />
        )}
        {activeTab === "tab3" && (
          <PositionsTable
            ref={positionsTableContainerRef}
            data={maPositions}
            title="Vol Arb Positions"
            globalFilter={positionsFilter}
            setGlobalFilter={setPositionsFilter}
            sorting={positionsSorting}
            setSorting={setPositionsSorting}
            scrollPosition={positionsScrollPosition}
          />
        )}
      </Box>
    </Box>
  );
};

export default VolArb_PNL;
